/**
 * Skill Editor
 * Edits a single skill configuration with AI-assisted generation
 */

import React, { useCallback, useState } from "react";
import { MarkdownField } from "@/components/fields/MarkdownField";
import { FieldType } from "@/config/fieldConfig.types";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Sparkles,
  Loader2,
  Layers,
  History,
} from "lucide-react";
import { useChatModelsQuery } from "@/lib/queries/useModelsQuery";
import { skillGenerationApi } from "@/lib/api/skillGeneration";
import { useToolComboTemplates } from "@/features/system-settings/hooks/useSystemConfig";
import { useI18n } from "@/i18n";
import type { SkillConfig } from "@/types";
import { SkillDedupConfig } from "@/features/agent-config/components/SkillDedupConfig";

// Validate skill_id format: lowercase letters, numbers, underscores, must start with a letter
const SKILL_ID_PATTERN = /^[a-z][a-z0-9_]*$/;
const normalizeSkillId = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9_]/g, "");
};

interface SkillDedupDefaults {
  data_query_hours?: number;
  report_window_hours?: number;
  max_per_window?: number;
  output_preview_length?: number;
  show_user_query?: boolean;
  query_preview_length?: number;
}

interface SkillEditorProps {
  skill: SkillConfig;
  onChange: (updates: Partial<SkillConfig>) => void;
  onDelete: () => void;
  agentContext?: string;
  /** Whether global dedup is enabled */
  dedupEnabled?: boolean;
  /** Global dedup default values (for placeholder hints) */
  globalDedupDefaults?: SkillDedupDefaults;
}


export const SkillEditor: React.FC<SkillEditorProps> = ({
  skill,
  onChange,
  onDelete,
  agentContext,
  dedupEnabled = false,
  globalDedupDefaults = {},
}) => {
  const { t } = useI18n();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDedupConfig, setShowDedupConfig] = useState(false);
  const [skillIdError, setSkillIdError] = useState<string | null>(null);

  // AI Generation state
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [selectedModel, setSelectedModel] = useState("");
  const [aiExplanation, setAiExplanation] = useState("");
  const [patternExplanation, setPatternExplanation] = useState("");

  // Queries and mutations
  const { data: chatModels = [] } = useChatModelsQuery();
  const { data: toolComboTemplates = [] } = useToolComboTemplates(true, "cn");
  const generateMutation = useMutation({
    mutationFn: skillGenerationApi.generate,
  });
  const generatePatternsMutation = useMutation({
    mutationFn: skillGenerationApi.generatePatterns,
  });

  const handleSkillIdChange = useCallback(
    (value: string) => {
      const normalized = normalizeSkillId(value);
      if (normalized && !SKILL_ID_PATTERN.test(normalized)) {
        setSkillIdError(t("agentConfig.skills.mustStartWithLetter"));
      } else {
        setSkillIdError(null);
      }
      onChange({ skill_id: normalized });
    },
    [onChange, t],
  );

  const handleKeywordsChange = useCallback(
    (value: string) => {
      const keywords = value
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);
      onChange({ keywords });
    },
    [onChange],
  );

  const handlePatternsChange = useCallback(
    (value: string) => {
      // Don't filter empty lines during editing to allow Enter key to create new lines
      // Empty lines will be filtered when saving
      const patterns = value.split("\n");
      onChange({ patterns });
    },
    [onChange],
  );

  const handleExampleQueriesChange = useCallback(
    (value: string) => {
      // Don't filter empty lines during editing to allow Enter key to create new lines
      // Empty lines will be filtered when saving
      const queries = value.split("\n");
      onChange({ example_queries: queries });
    },
    [onChange],
  );

  const handleDeleteConfirm = () => {
    if (
      confirm(t("agentConfig.skills.deleteConfirmTitle", { name: skill.name }))
    ) {
      onDelete();
    }
  };

  const handleAiGenerate = async () => {
    if (!skill.instructions?.trim()) {
      alert(t("agentConfig.skills.provideInstructions"));
      return;
    }
    if (!selectedModel) {
      alert(t("agentConfig.skills.selectModelFirst"));
      return;
    }

    try {
      const result = await generateMutation.mutateAsync({
        instructions: skill.instructions,
        model: selectedModel,
        language: "zh",
        context: agentContext,
      });

      // Fill form with generated data
      onChange({
        skill_id: skill.skill_id.startsWith("skill_")
          ? result.skill_id
          : skill.skill_id,
        name: skill.name === "New Skill" ? result.name : skill.name,
        description: result.description,
        keywords: result.keywords,
        patterns: result.patterns,
        example_queries: result.example_queries,
        exclusion_group: result.exclusion_group,
        force_response_type: result.force_response_type,
      });

      setAiExplanation(result.explanation);
    } catch (err) {
      console.error("AI generation failed:", err);
      alert(
        t("agentConfig.skills.generateFailed") +
          ": " +
          (err instanceof Error ? err.message : "Unknown error"),
      );
    }
  };

  const handleGeneratePatterns = async () => {
    if (!skill.instructions?.trim()) {
      alert(t("agentConfig.skills.provideInstructions"));
      return;
    }
    if (!selectedModel) {
      alert(t("agentConfig.skills.selectModelInAiPanel"));
      return;
    }

    try {
      const result = await generatePatternsMutation.mutateAsync({
        instructions: skill.instructions,
        // Filter empty strings to avoid backend validation issues
        keywords: skill.keywords.filter((k) => k.trim()),
        example_queries: skill.example_queries.filter((q) => q.trim()),
        model: selectedModel,
        language: "zh",
      });

      // Append new patterns to existing ones
      const existingPatterns = skill.patterns || [];
      const newPatterns = result.patterns.filter(
        (p) => !existingPatterns.includes(p),
      );
      onChange({ patterns: [...existingPatterns, ...newPatterns] });
      setPatternExplanation(result.explanation);
    } catch (err) {
      console.error("Pattern generation failed:", err);
      alert(
        t("agentConfig.skills.patternGenerateFailed") +
          ": " +
          (err instanceof Error ? err.message : "Unknown error"),
      );
    }
  };

  // Handler for per-skill dedup field changes
  const handleDedupFieldChange = useCallback(
    (field: string, value: number | boolean | null) => {
      // The field comes with the prefix already, e.g., "dedup_data_query_hours"
      onChange({ [field]: value } as Partial<SkillConfig>);
    },
    [onChange],
  );

  return (
    <Card className="p-4">
      <div className="space-y-4">
        {/* Header with actions */}
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900">
            {t("agentConfig.skills.editTitle")}
          </h3>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDeleteConfirm}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Instructions */}
        <MarkdownField
          name={`skill-instructions-${skill.skill_id}`}
          value={skill.instructions}
          onChange={(v) => onChange({ instructions: v })}
          schema={{
            type: FieldType.MARKDOWN,
            label: t("agentConfig.skills.instructions"),
            section: "skill",
            required: true,
            help: t("agentConfig.skills.instructionsHint"),
            overrideI18n: true,
          }}
        />

        {/* AI Generation Panel */}
        <div className="border border-purple-200 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
          <button
            type="button"
            onClick={() => setShowAiPanel(!showAiPanel)}
            className="flex items-center gap-2 w-full px-4 py-3 text-left"
          >
            <Sparkles className="w-5 h-5 text-purple-500" />
            <span className="flex-1 font-medium text-purple-800">
              {t("agentConfig.skills.aiGenerateRules")}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-purple-500 transition-transform ${
                showAiPanel ? "rotate-180" : ""
              }`}
            />
          </button>

          {showAiPanel && (
            <div className="px-4 pb-4 space-y-3">
              <p className="text-sm text-purple-700">
                {t("agentConfig.skills.aiGenerateDesc")}
              </p>

              {/* Model Selector */}
              <div>
                <label className="block text-xs font-medium text-purple-700 mb-1">
                  {t("agentConfig.skills.selectModel")}
                </label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white"
                >
                  <option value="">
                    {t("agentConfig.skills.selectModelPlaceholder")}
                  </option>
                  {chatModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Generate Button */}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleAiGenerate}
                  disabled={
                    generateMutation.isPending ||
                    !skill.instructions?.trim() ||
                    !selectedModel
                  }
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {generateMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {t("agentConfig.skills.generating")}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t("agentConfig.skills.generateFromInstructions")}
                    </>
                  )}
                </Button>
              </div>

              {/* AI Explanation */}
              {aiExplanation && (
                <div className="mt-3 p-3 bg-white border border-purple-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <span className="text-purple-500">✨</span>
                    <div>
                      <p className="text-xs font-medium text-purple-800 mb-1">
                        {t("agentConfig.skills.aiAnalysis")}
                      </p>
                      <p className="text-sm text-gray-700">{aiExplanation}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("agentConfig.skills.skillId")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={skill.skill_id}
              onChange={(e) => handleSkillIdChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md text-sm font-mono ${
                skillIdError ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="annual_report"
            />
            {skillIdError && (
              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {skillIdError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("agentConfig.skills.name")}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={skill.name}
              onChange={(e) => onChange({ name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Annual Report"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("agentConfig.skills.description")}
          </label>
          <input
            type="text"
            value={skill.description}
            onChange={(e) => onChange({ description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            placeholder={t("agentConfig.skills.descriptionPlaceholder")}
          />
          <p className="mt-1 text-xs text-gray-500">
            {t("agentConfig.skills.descriptionHint")}
          </p>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("agentConfig.skills.priority")}
            </label>
            <input
              type="number"
              value={skill.priority}
              onChange={(e) =>
                onChange({ priority: parseInt(e.target.value) || 100 })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              min={0}
              max={1000}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t("agentConfig.skills.priorityHint")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("agentConfig.skills.exclusionGroup")}
            </label>
            <input
              type="text"
              value={skill.exclusion_group || ""}
              onChange={(e) =>
                onChange({ exclusion_group: e.target.value || null })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="report_type"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t("agentConfig.skills.exclusionGroupHint")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("agentConfig.skills.forcePromptType")}
            </label>
            <select
              value={skill.force_response_type || ""}
              onChange={(e) =>
                onChange({
                  force_response_type:
                    (e.target.value as "quick" | "deep" | null) || null,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">
                {t("agentConfig.skills.forcePromptAuto")}
              </option>
              <option value="quick">
                {t("agentConfig.skills.forcePromptQuick")}
              </option>
              <option value="deep">
                {t("agentConfig.skills.forcePromptDeep")}
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              显示行
            </label>
            <select
              value={skill.display_row ?? 0}
              onChange={(e) =>
                onChange({ display_row: parseInt(e.target.value) })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={0}>第一行</option>
              <option value={1}>第二行</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              技能在移动端显示的行位置
            </p>
          </div>
        </div>

        {/* Tool Combo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
            <Layers className="w-4 h-4 text-blue-600" />
            {t("agentConfig.skills.toolCombo")}
          </label>
          <select
            value={skill.tool_combo_id || ""}
            onChange={(e) =>
              onChange({ tool_combo_id: e.target.value || null })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">{t("agentConfig.skills.toolComboNone")}</option>
            {toolComboTemplates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name} ({tpl.tool_count}{" "}
                {t("agentConfig.skills.toolComboTools")})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {t("agentConfig.skills.toolComboHint")}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`enabled-${skill.skill_id}`}
            checked={skill.enabled}
            onChange={(e) => onChange({ enabled: e.target.checked })}
            className="rounded border-gray-300"
          />
          <label
            htmlFor={`enabled-${skill.skill_id}`}
            className="text-sm text-gray-700"
          >
            {t("agentConfig.skills.enabled")}
          </label>
        </div>

        {/* Matching Rules Section */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            {showAdvanced ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            {t("agentConfig.skills.matchingRules")}
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("agentConfig.skills.keywords")}
                </label>
                <input
                  type="text"
                  value={skill.keywords.join(", ")}
                  onChange={(e) => handleKeywordsChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder={t("agentConfig.skills.keywordsPlaceholder")}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t("agentConfig.skills.keywordsHint")}
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("agentConfig.skills.regexPatterns")}
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleGeneratePatterns}
                    disabled={
                      generatePatternsMutation.isPending ||
                      !skill.instructions?.trim() ||
                      !selectedModel
                    }
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-7 px-2"
                  >
                    {generatePatternsMutation.isPending ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        {t("agentConfig.skills.generating")}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" />
                        {t("agentConfig.skills.aiGenerate")}
                      </>
                    )}
                  </Button>
                </div>
                <textarea
                  value={skill.patterns.join("\n")}
                  onChange={(e) => handlePatternsChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  rows={3}
                  placeholder="\\d{4}年.{0,5}(运势|报告)"
                />
                {patternExplanation && (
                  <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-700">
                    <span className="font-medium">AI: </span>
                    {patternExplanation}
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t("agentConfig.skills.regexPatternsHint")}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("agentConfig.skills.exampleQueries")}
                </label>
                <textarea
                  value={skill.example_queries.join("\n")}
                  onChange={(e) => handleExampleQueriesChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  rows={4}
                  placeholder="帮我看看明年运势&#10;生成一份年度报告&#10;2025年我的运气怎么样"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t("agentConfig.skills.exampleQueriesHint")}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Per-Skill Dedup Configuration Section */}
        {dedupEnabled && (
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowDedupConfig(!showDedupConfig)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {showDedupConfig ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              <History className="w-4 h-4 text-purple-500" />
              {t("agentConfig.skills.dedupConfig") || "去重配置"}（
              {t("agentConfig.skills.dedupConfigOverrideHint")?.split(
                "。",
              )[0] || "覆盖全局默认值"}
              ）
            </button>

            {showDedupConfig && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                {/* Per-skill dedup enable toggle */}
                <div className="mb-4 pb-4 border-b border-purple-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skill.dedup_enabled !== false}
                      onChange={(e) => {
                        // Toggle between enabled (null = follow global) and disabled (false)
                        const newValue = e.target.checked ? null : false;
                        onChange({ dedup_enabled: newValue });
                      }}
                      className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        {t("agentConfig.skills.perSkillDedupEnabled") ||
                          "启用去重"}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {skill.dedup_enabled === false
                          ? t("agentConfig.skills.perSkillDedupDisabledHint") ||
                            "此技能已禁用去重，不会注入历史记录"
                          : t("agentConfig.skills.perSkillDedupEnabledHint") ||
                            "跟随全局设置，可自定义配置覆盖全局默认值"}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Dedup config fields - only show when not explicitly disabled */}
                {skill.dedup_enabled !== false ? (
                  <>
                    <p className="text-xs text-purple-700 mb-4">
                      {t("agentConfig.skills.dedupConfigOverrideHint") ||
                        "留空表示使用全局默认值。仅填写需要覆盖的字段。"}
                    </p>
                    <SkillDedupConfig
                      fieldPrefix="dedup_"
                      isPerSkill={true}
                      globalDefaults={globalDedupDefaults}
                      values={{
                        data_query_hours: skill.dedup_data_query_hours,
                        report_window_hours: skill.dedup_report_window_hours,
                        max_per_window: skill.dedup_max_per_window,
                        output_preview_length:
                          skill.dedup_output_preview_length,
                        show_user_query: skill.dedup_show_user_query,
                        query_preview_length: skill.dedup_query_preview_length,
                      }}
                      onChange={handleDedupFieldChange}
                    />
                  </>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    {t("agentConfig.skills.dedupDisabledForSkill") ||
                      "此技能已禁用去重功能"}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
